import * as _ from 'lodash';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { Route, Routes as RoutesSwitch } from 'react-router-dom';
import { observer } from 'mobx-react';
import { DeveloperInviteModel, DeveloperModel } from '@journeyapps-platform/lib-journey-core';
import { Routes, useJourneyNavigation } from '../../../routes';
import { useOrgStore } from '../../../hooks/useOrgStore';
import { SearchableTableWidget } from '../../../widgets/table/SearchableTableWidget';
import { useSystem } from '../../../hooks/useSystem';
import { useAllowed } from '../../../hooks/useAllowed';
import { DeveloperRowEvent, DeveloperRowWidget } from './DeveloperRowWidget';
import { DeveloperInviteRowEvent, InviteRowWidget } from './InviteRowWidget';
import { AccessColumnEvent, TableRow } from '../../../widgets/table/TableWidget';
import { RoleDisplayWidget } from './RoleDisplayWidget';
import { EditDeveloperLayerWidget } from './dialogs/EditDeveloperLayerWidget';
import { UserModelAction } from '@journeyapps-platform/cardinal-catalog';
import { queue, QueueObject } from 'async';

export type Developer = DeveloperModel | DeveloperInviteModel;

export interface DevRow<Dev extends Developer = Developer> extends TableRow {
  dev: Dev;
}

function mapDeveloperTableCells(dev: Developer) {
  if (dev instanceof DeveloperModel) {
    return {
      id: dev.id,
      name: dev.model.name,
      email: dev.model.email
    };
  }

  return {
    id: dev.id,
    name: dev.model.metadata.user_name,
    email: dev.email
  };
}

const CBWidget: React.FC<React.PropsWithChildren<{ cb: () => any }>> = (props) => {
  useEffect(() => {
    props.cb();
  }, []);
  return props.children;
};

export const DevelopersTableWidget: React.FC = observer(() => {
  const orgStore = useOrgStore();
  const system = useSystem();
  const { params, navigate, relativeRoute } = useJourneyNavigation(Routes.MANAGE_USERS);

  const { org_id } = params;
  const org = orgStore.org(org_id);
  const [_q] = useState<QueueObject<DeveloperModel | DeveloperInviteModel>>(() => {
    return queue<DeveloperModel | DeveloperInviteModel>(async (item, cb) => {
      if (item instanceof DeveloperInviteModel) {
        await item.loadRoles();
      } else {
        await item.loadPolicies();
      }
      cb(null);
    }, 5);
  });

  const [tempDev] = useState<DeveloperModel>(
    system.developStore.generate({
      id: '',
      email: '',
      name: '',
      org_id: org_id
    })
  );

  // Should be able to invite also
  const isAllowedToAddDev = useAllowed({ action: UserModelAction.CREATE, resource: tempDev });

  useEffect(() => {
    (async () => {
      const org = await orgStore.resolve(org_id);
      org.loadRoles();
      org.loadDevelopers();
      org.loadDeveloperInvites();
    })();
  }, [org_id]);

  return (
    <>
      <SearchableTableWidget<DevRow>
        loading={!org || org?.developers.loading || org?.developerInvites.loading}
        controlButtons={[
          {
            label: 'Add Developer',
            disabled: !isAllowedToAddDev,
            tooltip: !isAllowedToAddDev ? 'Not permitted' : undefined,
            action: () => {
              navigate(Routes.CREATE_DEV, params);
            }
          }
        ]}
        columns={[
          {
            key: 'name',
            display: 'Name',
            accessorSearch: (name) => {
              return name;
            }
          },
          {
            key: 'email',
            display: 'Email',
            accessorSearch: (name) => {
              return name;
            }
          },
          {
            key: 'role',
            display: 'Roles',
            accessor: (event) => {
              return <RoleDisplayWidget developer={event.row.dev} filterOrgId={org_id} />;
            }
          },
          {
            key: 'row_action',
            display: '',
            shrink: true,
            accessor: (event) => {
              return event.row.cells.actionProvider(event);
            }
          }
        ]}
        rows={_.chain(org?.developers.items ?? [])
          .map((developer) => {
            return {
              ...mapDeveloperTableCells(developer),
              actionProvider: (event: AccessColumnEvent<DevRow>) => {
                return (
                  <CBWidget cb={() => _q.push(event.row.dev)}>
                    <DeveloperRowWidget event={event as DeveloperRowEvent} />
                  </CBWidget>
                );
              },
              dev: developer as Developer // For concat
            };
          })
          .concat(
            (org?.developerInvites.items ?? []).map((invite) => {
              return {
                ...mapDeveloperTableCells(invite),
                actionProvider: (event: AccessColumnEvent<DevRow>) => {
                  return (
                    <CBWidget cb={() => _q.push(event.row.dev)}>
                      <InviteRowWidget event={event as DeveloperInviteRowEvent} />
                    </CBWidget>
                  );
                },
                dev: invite
              };
            })
          )
          .orderBy((u) => u.name?.toLowerCase(), ['asc'])
          .map((u) => {
            return {
              key: u.id,
              dev: u.dev,
              cells: { ...u }
            };
          })
          .value()}
      />
      <RoutesSwitch>
        <Route
          path={relativeRoute(Routes.CREATE_DEV)}
          element={<EditDeveloperLayerWidget route={Routes.CREATE_DEV} />}
        />
        <Route path={relativeRoute(Routes.EDIT_USER)} element={<EditDeveloperLayerWidget route={Routes.EDIT_USER} />} />
        <Route
          path={relativeRoute(Routes.EDIT_INVITE)}
          element={<EditDeveloperLayerWidget route={Routes.EDIT_INVITE} />}
        />
      </RoutesSwitch>
    </>
  );
});
